import type { DraftOrder, DraftWaypoint } from "./types";
import { clampPickupSequence, waypointSequences } from "./waypoint-order";

/**
 * 受注の作成/更新ペイロード構築を 1 箇所に集約する共通ビルダー。
 * orders.new / orders.edit / 一括取込モーダルの確定処理で共用する。
 *
 * 座標はジオコーディングが非同期のため、解決済みの値を呼び出し側 (OrderEditor 等) から渡す。
 * - create: 空文字・未設定の任意項目は `undefined` で省略 (CreateOrderInput は `.optional()`)
 * - update: 空文字・未設定の任意項目は `null` でクリア (UpdateOrderInput は `.nullable().optional()`)
 * - order_number: 作成・更新とも必須。フォーム側の必須チェック後、trim 済み文字列を送る。
 */
export interface ResolvedCoords {
  pickup: { lat: number; lng: number } | null;
  delivery: { lat: number; lng: number } | null;
}

/**
 * 経由地を API の形へ写す (#723)。訪問順は「配列の並び + 集荷の訪問順」から導出する。
 * 集荷と経由地で 1..N の連番を分け合い、納品は API 側が最後に置く。
 * 空欄は null で送る (contract は nullable、`undefined` だと既定値が入るため区別する)。
 */
const draftWaypoints = (waypoints: DraftWaypoint[], pickupSequence: number) => {
  const sequences = waypointSequences(pickupSequence, waypoints.length);
  return waypoints.map((w, i) => ({
    public_id: w.public_id,
    sequence: sequences[i] ?? i + 1,
    action: w.action,
    requester: w.requester,
    pickup_spot_public_id: w.pickup_spot_public_id || null,
    name: w.name || null,
    address: w.address || null,
    phone: w.phone || null,
    latitude: w.latitude,
    longitude: w.longitude,
    time_start: w.time_start || null,
    time_end: w.time_end || null,
    duration_minutes: w.duration_minutes,
    note: w.note || null,
  }));
};

const trimSeconds = (value: string) => {
  const trimmed = value.trim();
  return /^\d{2}:\d{2}:\d{2}$/.test(trimmed) ? trimmed.slice(0, 5) : trimmed;
};

/**
 * 不可時間の区間を保存できる形へ整える (#793)。
 *
 * - **両端が空の行は落とす**。「不可時間を追加」を押しただけの行で、本番にも
 *   `[{"from":"","to":""}]` が 2 件残っている。読み込んだ値をそのまま送り返すと入口
 *   (`UnavailableTimeWindowInput`) が空文字を弾いて 400 になり、その注文を保存できなくなる。
 * - **秒付き (`HH:MM:SS`) は `HH:MM` に詰める**。旧 admin が保存時に `:00` を付けていた。
 * - **片側だけの行は落とさない**。落とすと「保存したのに区間が消える」ので、入口に
 *   「時刻を入力してください」と項目を名指しさせる。
 */
export const sanitizeTimeWindows = (windows: { from: string; to: string }[]) =>
  windows
    .map((w) => ({ from: trimSeconds(w.from), to: trimSeconds(w.to) }))
    .filter((w) => w.from !== "" || w.to !== "");

const draftItems = (items: DraftOrder["items"]) =>
  items.map((i) => ({
    product_name: i.product_name,
    quantity: i.quantity,
    unit_price: i.unit_price,
  }));

/** DraftOrder + 解決済み座標から CreateOrderInput を組み立てる (orders.new と同一挙動)。 */
export function buildCreateOrderPayload(v: DraftOrder, coords: ResolvedCoords) {
  const pickupUnavailableTimes = sanitizeTimeWindows(v.pickup_unavailable_times);
  const deliveryUnavailableTimes = sanitizeTimeWindows(v.delivery_unavailable_times);
  return {
    uploaded_file_public_id: v.uploaded_file_public_id || undefined,
    delivery_spot_public_id: v.delivery_spot_public_id || undefined,
    client_id: v.client_id || undefined,
    pickup_spot_public_id: v.pickup_spot_public_id || undefined,
    pickup_latitude: coords.pickup?.lat ?? undefined,
    pickup_longitude: coords.pickup?.lng ?? undefined,
    delivery_latitude: coords.delivery?.lat ?? undefined,
    delivery_longitude: coords.delivery?.lng ?? undefined,
    order_number: v.order_number.trim(),
    payment_number: v.payment_number || undefined,
    ...(v.payment_type ? { payment_type: v.payment_type } : {}),
    delivery_address: v.delivery_address,
    delivery_date: v.delivery_date,
    delivery_time_start: v.delivery_time_start || undefined,
    delivery_time_end: v.delivery_time_end || undefined,
    delivery_building: v.delivery_building || undefined,
    handover_location: v.handover_location || undefined,
    arrival_instructions: v.arrival_instructions || undefined,
    delivery_company: v.delivery_company || undefined,
    delivery_branch: v.delivery_branch || undefined,
    delivery_person_name: v.delivery_person_name || undefined,
    delivery_person_name_kana: v.delivery_person_name_kana || undefined,
    delivery_email: v.delivery_email || undefined,
    delivery_phone: v.delivery_phone || undefined,
    store_name: v.store_name || undefined,
    store_address: v.store_address || undefined,
    store_phone: v.store_phone || undefined,
    pickup_time_start: v.pickup_time_start || undefined,
    pickup_time_end: v.pickup_time_end || undefined,
    pickup_unavailable_times:
      pickupUnavailableTimes.length > 0 ? pickupUnavailableTimes : undefined,
    pickup_duration_minutes: v.pickup_duration_minutes ?? undefined,
    delivery_packaging: v.delivery_packaging || undefined,
    store_message: v.store_message || undefined,
    delivery_notes: v.delivery_notes || undefined,
    day_of_contact_person: v.day_of_contact_person || undefined,
    day_of_contact_phone: v.day_of_contact_phone || undefined,
    delivery_duration_minutes: v.delivery_duration_minutes ?? undefined,
    delivery_unavailable_times:
      deliveryUnavailableTimes.length > 0 ? deliveryUnavailableTimes : undefined,
    remarks: v.remarks || undefined,
    // #767: 標準 (null) は送らず API 側の既定 (3 時間) に委ねる。
    max_ride_minutes: v.max_ride_minutes ?? undefined,
    items: draftItems(v.items),
    pickup_sequence: clampPickupSequence(v.pickup_sequence, v.waypoints.length),
    waypoints: draftWaypoints(v.waypoints, v.pickup_sequence),
  };
}

/**
 * DraftOrder + 解決済み座標から UpdateOrderInput を組み立てる (orders.edit と同一挙動)。
 * uploaded_file_public_id は再アップロードで form 値と乖離しうるため呼び出し側から渡す。
 * coords は受注固有座標だけを受け取る。地図表示用のマスタ座標 fallback は state / payload へ渡さない。
 * マスタ選択中でも手動ピン・PDF座標があれば保存し、無ければ null のまま DB のマスタ参照を使う。
 */
export function buildUpdateOrderPayload(
  v: DraftOrder,
  coords: ResolvedCoords,
  uploadedFilePublicId: string | null,
) {
  const ns = (value: string) => value || null;
  return {
    uploaded_file_public_id: uploadedFilePublicId,
    delivery_spot_public_id: v.delivery_spot_public_id || null,
    client_id: v.client_id || null,
    pickup_spot_public_id: v.pickup_spot_public_id || null,
    pickup_latitude: coords.pickup?.lat ?? null,
    pickup_longitude: coords.pickup?.lng ?? null,
    delivery_latitude: coords.delivery?.lat ?? null,
    delivery_longitude: coords.delivery?.lng ?? null,
    order_number: v.order_number.trim(),
    payment_number: ns(v.payment_number),
    payment_type: v.payment_type || null,
    delivery_address: v.delivery_address,
    delivery_date: v.delivery_date,
    delivery_time_start: ns(v.delivery_time_start),
    delivery_time_end: ns(v.delivery_time_end),
    delivery_building: ns(v.delivery_building),
    handover_location: ns(v.handover_location),
    arrival_instructions: ns(v.arrival_instructions),
    delivery_company: ns(v.delivery_company),
    delivery_branch: ns(v.delivery_branch),
    delivery_person_name: ns(v.delivery_person_name),
    delivery_person_name_kana: ns(v.delivery_person_name_kana),
    delivery_email: ns(v.delivery_email),
    delivery_phone: ns(v.delivery_phone),
    store_name: ns(v.store_name),
    store_address: ns(v.store_address),
    store_phone: ns(v.store_phone),
    pickup_time_start: ns(v.pickup_time_start),
    pickup_time_end: ns(v.pickup_time_end),
    pickup_unavailable_times: sanitizeTimeWindows(v.pickup_unavailable_times),
    pickup_duration_minutes: v.pickup_duration_minutes,
    delivery_packaging: ns(v.delivery_packaging),
    store_message: ns(v.store_message),
    delivery_notes: ns(v.delivery_notes),
    day_of_contact_person: ns(v.day_of_contact_person),
    day_of_contact_phone: ns(v.day_of_contact_phone),
    delivery_duration_minutes: v.delivery_duration_minutes,
    delivery_unavailable_times: sanitizeTimeWindows(v.delivery_unavailable_times),
    remarks: ns(v.remarks),
    // #767: null を送って標準 (3 時間) へ戻せるようにする。
    max_ride_minutes: v.max_ride_minutes,
    status: v.status,
    items: draftItems(v.items),
    pickup_sequence: clampPickupSequence(v.pickup_sequence, v.waypoints.length),
    waypoints: draftWaypoints(v.waypoints, v.pickup_sequence),
  };
}

/**
 * 一括取込の「既存更新」は読み取り内容の反映であり、受注の業務ステータスは既存値を維持する。
 * parsedPreviewToDraft は新規作成向けに status=draft を持つため、ここで明示的に除外する。
 *
 * 経由地 (#723) も同じ理由で送らない。注文書に経由地は書かれておらず
 * `parsedPreviewToDraft` は常に空で始まるため、送ると**手で入れた経由地が再取込のたびに消える**。
 * API 側は `waypoints` 未指定を「据え置き」として扱う。
 *
 * 積載時間の上限 (#767) も同じ。注文書に 3 時間 / 5 時間は書かれておらず、送ると
 * **手で入れた延長が再取込のたびに標準へ戻る**。API 側は未指定を「据え置き」として扱う。
 */
export function buildImportUpdateOrderPayload(
  v: DraftOrder,
  coords: ResolvedCoords,
  uploadedFilePublicId: string | null,
) {
  const {
    status: _status,
    waypoints: _waypoints,
    pickup_sequence: _pickupSequence,
    max_ride_minutes: _maxRideMinutes,
    ...payload
  } = buildUpdateOrderPayload(v, coords, uploadedFilePublicId);
  return payload;
}
